import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { Receipt, Layers, Download } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { makeApi, authedUrl } from "@/lib/api";
import { money, dateShort } from "@/lib/format";
import { InvoiceStatusPill } from "@/components/invoices/InvoiceControls";

const day = (d) => (d ? dateShort(`${d}T12:00:00`) : "—");
const sum = (xs) => xs.reduce((s, i) => s + (Number(i.amount) || 0), 0);

const Total = ({ label, value, sub, tone, testId }) => (
  <div className={`rounded-xl border p-4 ${tone}`} data-testid={testId}>
    <div className="text-xs font-medium uppercase tracking-wider opacity-80">{label}</div>
    <div className="mt-1 text-2xl md:text-3xl font-bold tabular-nums" style={{ fontFamily: "Outfit" }} data-testid={`${testId}-value`}>{value}</div>
    <div className="text-xs opacity-70 mt-0.5">{sub}</div>
  </div>
);

export const downloadBatch = async (getToken, batchId) => {
  window.open(await authedUrl(`/invoice-batches/${batchId}/packet`, getToken), "_blank");
};

export default function InvoicesList() {
  const { getToken } = useAuth();
  const [invoices, setInvoices] = useState(null);
  const [batches, setBatches] = useState([]);
  useEffect(() => {
    const api = makeApi(getToken);
    api.get("/invoices").then(({ data }) => setInvoices(data)).catch(() => setInvoices([]));
    api.get("/invoice-batches").then(({ data }) => setBatches(data)).catch(() => {});
  }, [getToken]);

  const t = useMemo(() => {
    const list = invoices || [];
    const sent = list.filter((i) => i.status === "Sent");
    const overdue = sent.filter((i) => i.overdue);
    const paid = list.filter((i) => i.status === "Paid");
    return { outstanding: sum(sent), sentN: sent.length, overdue: sum(overdue), overdueN: overdue.length, paid: sum(paid), paidN: paid.length, drafts: list.filter((i) => i.status === "Draft").length };
  }, [invoices]);
  const batchNo = Object.fromEntries(batches.map((b, i) => [b.id, batches.length - i]));

  return (
    <div className="space-y-6" data-testid="invoices-list-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>Invoices</h1>
        <p className="text-sm text-slate-500 mt-1">Track what&apos;s been sent, what&apos;s paid and what&apos;s overdue.</p>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        <Total label="Outstanding" value={money(t.outstanding)} sub={`${t.sentN} sent, awaiting payment${t.drafts ? ` · ${t.drafts} draft` : ""}`} tone="bg-blue-50 border-blue-200 text-blue-900" testId="inv-total-outstanding" />
        <Total label="Overdue" value={money(t.overdue)} sub={`${t.overdueN} past due date`} tone="bg-rose-50 border-rose-200 text-rose-900" testId="inv-total-overdue" />
        <Total label="Paid" value={money(t.paid)} sub={`${t.paidN} paid invoice${t.paidN === 1 ? "" : "s"}`} tone="bg-emerald-50 border-emerald-200 text-emerald-900" testId="inv-total-paid" />
      </div>

      {batches.length > 0 && (
        <Card className="border-slate-200 p-4" data-testid="batches-card">
          <div className="text-sm font-semibold flex items-center gap-2 mb-3"><Layers className="w-4 h-4 text-[#002FA7]" /> Billing batches</div>
          <div className="flex gap-3 overflow-x-auto pb-1">
            {batches.slice(0, 12).map((b) => (
              <div key={b.id} className="shrink-0 rounded-xl border border-slate-200 px-4 py-3 min-w-[220px]" data-testid={`batch-${b.id}`}>
                <div className="text-xs text-slate-500">Batch {batchNo[b.id]} · {dateShort(b.created_at)} · {b.remit_profile === "factored" ? "Factored" : "Direct"}</div>
                <div className="font-semibold mt-0.5">{b.count} invoice{b.count === 1 ? "" : "s"} · {money(b.total)}</div>
                <Button variant="link" size="sm" className="px-0 h-auto mt-1" onClick={() => downloadBatch(getToken, b.id)} data-testid={`batch-download-${b.id}`}><Download className="w-3.5 h-3.5 mr-1" /> Combined PDF</Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>{["Invoice #", "Broker", "Load #", "Amount", "Status", "Sent", "Due", "Aging", "Remit"].map((h) => <th key={h} className="text-left px-4 py-3 font-medium whitespace-nowrap">{h}</th>)}</tr>
            </thead>
            <tbody>
              {invoices === null && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>}
              {invoices?.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No invoices yet.</td></tr>}
              {(invoices || []).map((i) => (
                <tr key={i.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`invoice-row-${i.invoice_number}`}>
                  <td className="px-4 py-3 font-semibold whitespace-nowrap">
                    <Link to={`/admin/invoices/${i.id}`} className="text-[#002FA7] hover:underline inline-flex items-center gap-1.5"><Receipt className="w-4 h-4" /> {i.invoice_number}</Link>
                    {i.batch_id && batchNo[i.batch_id] && <div className="text-[11px] font-normal text-slate-500">Batch {batchNo[i.batch_id]}</div>}
                  </td>
                  <td className="px-4 py-3">{i.broker_name}</td>
                  <td className="px-4 py-3 text-slate-600 whitespace-nowrap" data-testid={`invoice-load-${i.invoice_number}`}>{i.load?.carrier_number || i.load?.load_number}{i.load?.carrier_number && <div className="text-[11px] text-slate-500">Ref #{i.load.load_number}</div>}</td>
                  <td className="px-4 py-3 font-medium tabular-nums">{money(i.amount)}</td>
                  <td className="px-4 py-3"><InvoiceStatusPill inv={i} testId={`invoice-status-${i.invoice_number}`} /></td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{day(i.sent_on)}</td>
                  <td className={`px-4 py-3 whitespace-nowrap ${i.overdue ? "text-rose-700 font-medium" : "text-slate-500"}`}>{i.status === "Paid" ? `Paid ${day(i.paid_at)}` : day(i.due_date)}</td>
                  <td className="px-4 py-3 whitespace-nowrap tabular-nums" data-testid={`invoice-aging-${i.invoice_number}`}>{i.days_outstanding == null ? "—" : `${i.days_outstanding} day${i.days_outstanding === 1 ? "" : "s"}`}</td>
                  <td className="px-4 py-3 text-slate-500">{i.remit_profile === "factored" ? "Factored" : "Direct"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
